// Microsoft-To-Do-style personal task lists.
// The list tree itself lives in the app-shell Sidebar (grouped Persönlich /
// Geteilt, same as the Projekte tree); this route only renders the task
// list/board/calendar for whichever list the URL selects, plus the detail
// modal.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { useMe } from "../lib/auth";
import { openTaskImport } from "../store/taskImport";
import {
  aiSummarizeList,
  aiSuggestSteps,
  aiSummarizePersonalTask,
  api,
  ApiError,
} from "../lib/api";
import {
  Plus,
  Trash2,
  Check,
  Circle,
  Repeat,
  Bell,
  CalendarDays,
  CalendarPlus,
  ListTodo,
  List,
  Columns3,
  X,
  Flag,
  User as UserIcon,
  ArrowRightToLine,
  ArrowUpRight,
  ListChecks,
  CircleDot,
  Star,
  Link2,
  Sun,
  GitMerge,
  AlertTriangle,
  Eye,
  Sparkles,
} from "lucide-preact";
import type { TaskList, TaskPriority, RecurrenceRule, PersonalTaskStep } from "@juuplan/shared";
import { Layout } from "../components/Layout";
import { usePageHeader } from "../lib/pageHeader";
import { PageShell } from "../components/ui/PageShell";
import { uxEnabled } from "../lib/ux";
import { displayOrder, neighbourUid } from "../lib/taskDetailModel";
import { computeView } from "../lib/viewState";
import { TaskDetailPanel } from "../components/taskDetail/TaskDetailPanel";
import { DetailSheet } from "../components/taskDetail/DetailSheet";
import { refreshPersonalTasks } from "../store/personalTasks";
import { ViewTabs } from "../components/ui/ViewTabs";
import { Button } from "../components/ui/Button";
import { openPersonalTaskSig, openProjectTaskSig, parseAndStripTaskParam } from "../store/deepLinks";
import { carryOverStepsSig } from "../store/carryOverSteps";
import { formatDateShort, localDateIso, localTodayIso, TASK_PRIORITY_VAR, TASK_PRIORITY_LABEL, softPillStyle } from "../lib/format";
import { Modal } from "../components/Modal";
import { TitleField } from "../components/ui/TitleField";
import { DATE_EMPTY_SUPPRESS_CLS } from "../components/TaskDateCell";
import { AccessOverviewDialog } from "../components/AccessOverviewDialog";
import { RichTextEditor } from "../components/RichTextEditor";
import { AttachmentsSection } from "../components/AttachmentsSection";
import { NotesSection } from "../components/NotesSection";
import { AssigneePicker, AvatarStack } from "../components/AssigneePicker";
import {
  useLists,
  usePersonalTasks,
  setPersonalTaskQuery,
  createPersonalTask,
  updatePersonalTask,
  deletePersonalTask,
  fetchPersonalTaskDetail,
  fetchSpaceMembers,
  addStep,
  addLinkedStep,
  updateStep,
  deleteStep,
  promoteStep,
  setRecurrence,
  clearRecurrence,
  useListStatusesMap,
  useListStatuses,
  ensureListStatuses,
  type PersonalTaskDetail,
  type SpaceMember,
} from "../store/personalTasks";
import { useUserPrefs } from "../store/userPrefs";
import { resolveTargetList } from "../lib/defaultList";
import { createReminder } from "../store/reminders";
import { useFavorites, toggleFavorite } from "../store/favorites";
import { PendingReminders } from "../components/PendingReminders";
import { FollowUpComposer } from "../components/FollowUpComposer";
import { toastSuccess, toastError } from "../lib/toast";
import { GroupSortFilterBar } from "../components/GroupSortFilterBar";
import { SavedViewsMenu } from "../components/SavedViewsMenu";
import { TaskBoardView } from "../views/TaskBoardView";
import { TaskCalendarView } from "../views/TaskCalendarView";
import { TaskListView } from "../views/TaskListView";
import { BulkActionToolbar } from "../components/BulkActionToolbar";
import { useRecordSelection } from "../lib/useRecordSelection";
import { personalTaskToRecord, type ResolverContext, type TaskRecord } from "../lib/taskRecord";
import {
  DEFAULT_VIEW_STATE,
  type Bucket,
  type BucketOptions,
  type GroupBy,
  type ViewKind,
  type ViewState,
} from "../lib/viewState";
import { loadViewState, saveViewState } from "../lib/viewStateStore";
import { findUser } from "../store/users";
import { personalAssigneeIds } from "../lib/personalAssignees";
import { addToMyDay } from "../store/myDay";
import { MergeTaskDialog } from "../components/MergeTaskDialog";

/**
 * Auswahl in der Seitenleiste: eine konkrete Liste oder „Alle Aufgaben".
 * Das frühere `null` war die Pseudo-Liste „Ohne Liste" (/lists/none) — die gibt
 * es nicht mehr, seit jede persönliche Aufgabe in eine Liste gehört.
 */
type Selection = number | "all";

/** Returns true when the list is a shared space list. */
function isShared(list: TaskList): boolean {
  return list.scope === "space";
}

/** Returns true when the user has write access (owner / editor / personal). */
function canWrite(list: TaskList | undefined): boolean {
  if (!list) return true; // „Alle Aufgaben" (Pseudo-Liste) ist immer schreibbar
  if (!isShared(list)) return true;
  return list.role === "owner" || list.role === "editor";
}

/** Shown when a task would have to be created but the user has no list yet. */
const NO_LIST_HINT =
  "Du hast noch keine Liste. Lege in der Seitenleiste unter „Listen“ eine Liste an oder wähle in den Einstellungen eine Standard-Liste.";

const PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
  { value: "urgent", label: "Dringend" },
];

// Redesign-Paket 2: der Ansichtsumschalter (vormals `ViewSwitcher` in einer
// eigenen Werkzeugzeile) sitzt jetzt als `ViewTabs` im Seitenkopf (Referenz:
// docs/design/01-roadmap-expanded.png zeigt genau diese Tableiste mit
// Unterstrich neben dem Seitentitel). „Gantt" bietet diese Route nicht, daher
// nur die drei Einträge.
const LIST_VIEW_TABS: { id: ViewKind; label: string; icon: typeof List }[] = [
  { id: "list", label: "Liste", icon: List },
  { id: "board", label: "Board", icon: Columns3 },
  { id: "calendar", label: "Kalender", icon: CalendarDays },
];

function roleBadgeLabel(list: TaskList): string {
  const role = list.role ?? "viewer";
  const map: Record<string, string> = {
    owner: "Geteilt · Eigentümer",
    editor: "Geteilt · Editor",
    viewer: "Geteilt · Leser",
  };
  return map[role] ?? "Geteilt";
}

export function Lists({ id }: { id?: string }): preact.JSX.Element {
  const me = useMe();
  const { lists, loading: listsLoading } = useLists();
  const { tasks, loading: tasksLoading } = usePersonalTasks();

  // Selection comes from the URL (/lists, /lists/:id) — the list tree lives in
  // the Sidebar and navigates via <Link>, mirroring how /p/:id drives the
  // active project. An unparseable id falls back to "all"; dazu gehört auch das
  // alte /lists/none („Ohne Liste"), auf das noch ein Lesezeichen oder eine
  // ältere Benachrichtigung zeigen kann.
  const selectedList: Selection = useMemo(() => {
    if (id === undefined) return "all";
    const n = Number(id);
    return Number.isFinite(n) ? n : "all";
  }, [id]);
  const [includeDone, setIncludeDone] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  // UX-Vorschlag P3: gemeinsamer Detailbereich rechts statt des Modals.
  // „Vollständig öffnen" (p3Full) und Nur-Lese-Zugriff zeigen weiter das alte
  // Detail; ohne Flag bleibt alles wie bisher.
  const p3 = uxEnabled("ux_web_p3");
  const [p3Full, setP3Full] = useState(false);
  useEffect(() => setP3Full(false), [selectedTaskId]);
  // ↑/↓ folgt der angezeigten Reihenfolge (von TaskListView gemeldet).
  const [listOrder, setListOrder] = useState<string[] | null>(null);

  // AI list summary state
  const [aiListSummaryBusy, setAiListSummaryBusy] = useState(false);
  const [aiListSummary, setAiListSummary] = useState<string | null>(null);
  const [aiListSummary503, setAiListSummary503] = useState(false);
  // Request-id counter: incremented on each call; allows in-flight responses
  // for a previous list selection to be discarded when the user switches lists.
  const summarizeListReqRef = useRef(0);

  // Quick-add task input.
  const [quickTitle, setQuickTitle] = useState("");

  // The currently selected TaskList object (undefined for pseudo-lists).
  const selectedListObj = typeof selectedList === "number"
    ? lists.find((l) => l.id === selectedList)
    : undefined;

  const writeAccess = canWrite(selectedListObj);

  // "Alle Aufgaben" has no list context, but a new task must still land in a
  // list: the configured Standard-Liste, else the first personal list. Null =
  // the user has no usable list yet → quick-add shows a hint instead.
  const prefs = useUserPrefs();
  const targetList = useMemo(
    () => (selectedList === "all" ? resolveTargetList(lists, prefs.default_list_id) : null),
    [selectedList, lists, prefs.default_list_id]
  );
  const missingTargetList = selectedList === "all" && !listsLoading && targetList === null;

  // P4: multi-view (Liste / Board / Kalender) over the personal tasks of the
  // current selection. The detailed "Liste" pane stays the existing UI; board
  // and calendar reuse the generic record views. ViewState persisted per scope.
  const scopeKey = `lists:${selectedList}`;
  const [viewState, setViewState] = useState<ViewState>(() =>
    loadViewState(scopeKey, { ...DEFAULT_VIEW_STATE, view: "list", groupBy: "status" })
  );
  // Re-load the persisted view when switching lists.
  useEffect(() => {
    setViewState(loadViewState(`lists:${selectedList}`, { ...DEFAULT_VIEW_STATE, view: "list", groupBy: "status" }));
  }, [selectedList]);
  const updateViewState = (next: ViewState): void => {
    setViewState(next);
    saveViewState(`lists:${selectedList}`, next);
  };

  const records: TaskRecord[] = useMemo(() => tasks.map(personalTaskToRecord), [tasks]);

  // Ensure each list referenced by the visible tasks (and the current selection)
  // has its status set loaded, so status pills/dropdowns and status grouping work.
  const statusMap = useListStatusesMap();
  useEffect(() => {
    const ids = new Set<number>();
    for (const t of tasks) if (t.list_id != null) ids.add(t.list_id);
    if (typeof selectedList === "number") ids.add(selectedList);
    for (const id of ids) ensureListStatuses(id);
  }, [tasks, selectedList]);

  const personalCtx: ResolverContext = useMemo(
    () => ({
      resolveProject: () => undefined,
      resolvePhase: () => undefined,
      resolveLabel: () => undefined,
      resolveUserName: (id) => findUser(id)?.name ?? `User #${id}`,
      resolveStatuses: (record) =>
        record.listId == null
          ? []
          : (statusMap[record.listId] ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              color: s.color,
              category: s.category,
            })),
      resolveList: (id) => {
        const l = lists.find((x) => x.id === id);
        return l ? { name: l.name, color: l.color } : undefined;
      },
      patch: async (record, change) => {
        const body: Parameters<typeof updatePersonalTask>[1] = {};
        if (change.title !== undefined) body.title = change.title;
        if (change.priority !== undefined) body.priority = change.priority ?? "medium";
        if (change.dueDate !== undefined) body.due_date = change.dueDate;
        if (change.startDate !== undefined) {
          // Personal tasks have no start date — the due date is the single date.
          body.due_date = change.startDate;
        }
        if (change.assigneeIds !== undefined) body.assignee_ids = change.assigneeIds;
        if (change.statusId !== undefined) body.status_id = change.statusId;
        // is_done via status is preferred; fall back to the legacy toggle.
        if (change.done !== undefined && change.statusId === undefined)
          body.is_done = change.done;
        if (Object.keys(body).length > 0) await updatePersonalTask(record.id, body);
      },
      patchMany: async (recs, change) => {
        // No batch endpoint for personal tasks yet — apply sequentially, then
        // the store's single refresh (inside the last update) settles the view.
        for (const r of recs) {
          const body: Parameters<typeof updatePersonalTask>[1] = {};
          if (change.priority !== undefined) body.priority = change.priority ?? "medium";
          if (change.statusId !== undefined) body.status_id = change.statusId;
          if (change.assigneeIds !== undefined) body.assignee_ids = change.assigneeIds;
          if (change.done !== undefined && change.statusId === undefined)
            body.is_done = change.done;
          if (change.dueDate !== undefined) body.due_date = change.dueDate;
          if (Object.keys(body).length > 0) await updatePersonalTask(r.id, body);
        }
      },
      deleteMany: async (recs) => {
        for (const r of recs) await deletePersonalTask(r.id);
      },
    }),
    [statusMap, lists]
  );
  // Aufgaben ausserhalb der sichtbaren Liste (erledigt, Schritt, andere Liste —
  // typisch bei Deep-Links aus Suche, Mein Tag, Glocke) einzeln nachladen.
  // Vorher fielen sie still auf das alte Detail zurueck.
  const [p3Extra, setP3Extra] = useState<TaskRecord | null>(null);
  const [p3ExtraFailed, setP3ExtraFailed] = useState(false);
  const p3InList =
    selectedTaskId !== null ? records.find((r) => r.id === selectedTaskId) ?? null : null;
  const loadP3Extra = (taskId: number): void => {
    fetchPersonalTaskDetail(taskId).then(
      (d) => setP3Extra(d.id === taskId ? personalTaskToRecord(d) : null),
      // Kein Zugriff/geloescht: altes Detail zeigt die Fehlermeldung wie bisher.
      () => {
        setP3Extra(null);
        setP3ExtraFailed(true);
      }
    );
  };
  useEffect(() => {
    setP3Extra(null);
    setP3ExtraFailed(false);
    if (p3 && selectedTaskId !== null && !p3InList) loadP3Extra(selectedTaskId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p3, selectedTaskId, p3InList === null]);
  const p3Wanted = p3 && !p3Full && writeAccess && selectedTaskId !== null;
  const p3Record = p3Wanted ? p3InList ?? (p3Extra?.id === selectedTaskId ? p3Extra : null) : null;
  // Waehrend des Nachladens nichts zeigen statt kurz das alte Detail.
  const showClassicDetail = selectedTaskId !== null && !p3Record && (!p3Wanted || p3ExtraFailed);
  // Nachgeladene Aufgabe steht nicht im Store: nach jeder Aenderung neu holen.
  const p3Ctx: ResolverContext =
    p3InList || !p3Record
      ? personalCtx
      : {
          ...personalCtx,
          patch: async (r, change) => {
            await personalCtx.patch(r, change);
            loadP3Extra(r.id);
          },
        };
  const LIST_GROUP_OPTS: GroupBy[] = [
    "none",
    "status",
    "priority",
    "due",
    "assignee",
    "list",
  ];

  // Bucketing options: within a single selected list, feed its ordered status
  // set so status grouping matches the list's configured statuses. When
  // "Erledigte anzeigen" is off, done tasks aren't loaded — so drop the
  // done/cancelled statuses too, otherwise status grouping renders empty
  // "Erledigt" columns/sections that carry no tasks.
  const bucketOpts: BucketOptions = useMemo(
    () => ({
      statuses:
        typeof selectedList === "number"
          ? (statusMap[selectedList] ?? [])
              .filter((s) => includeDone || (s.category !== "done" && s.category !== "cancelled"))
              .map((s) => ({
                id: s.id,
                name: s.name,
                color: s.color,
                category: s.category,
              }))
          : undefined,
      resolveUserName: (id) => findUser(id)?.name ?? `User #${id}`,
      resolveList: (id) => {
        const l = lists.find((x) => x.id === id);
        return l ? { name: l.name, color: l.color } : undefined;
      },
    }),
    [selectedList, statusMap, lists, includeDone]
  );

  // Create a task inside a group, deriving defaults from the group's key
  // (status:<id> → that status, prio:<p> → that priority, list:<id> → that
  // list, user:<id> → that assignee, due:today/week → a matching due date) —
  // otherwise the new task silently jumps into a different group.
  async function handleAddInGroup(b: Bucket, title: string): Promise<void> {
    const input: Parameters<typeof createPersonalTask>[0] = { title };
    if (typeof selectedList === "number") input.list_id = selectedList;
    // "Alle Aufgaben": no list context — route into the Standard-Liste (a
    // "list:<id>" group below still wins, it names its own list explicitly).
    else if (selectedList === "all") {
      if (targetList === null) {
        toastError(NO_LIST_HINT);
        return;
      }
      input.list_id = targetList.id;
    }
    const [kind, val] = b.key.split(":");
    if (kind === "prio" && val && val !== "none") input.priority = val;
    else if (kind === "list" && val && val !== "none") input.list_id = Number(val);
    else if (kind === "user" && val && val !== "none") input.assignee_ids = [Number(val)];
    else if (kind === "due" && val === "today") input.due_date = localTodayIso();
    else if (kind === "due" && val === "week") {
      // End of the current ISO week — stays inside the "Diese Woche" bucket.
      const d = new Date();
      d.setDate(d.getDate() + (6 - ((d.getDay() + 6) % 7)));
      input.due_date = localDateIso(d);
    }
    const created = await createPersonalTask(input);
    // A status group can only be honored on a listed task — PATCH after create.
    // Only within a single selected list: across "Alle Aufgaben" the status
    // buckets come from mixed lists, so the id need not belong to the target
    // list (the task then simply keeps that list's default status).
    if (
      kind === "status" &&
      val &&
      val !== "_fallback" &&
      typeof selectedList === "number" &&
      input.list_id != null &&
      created?.id
    ) {
      await updatePersonalTask(created.id, { status_id: Number(val) });
    }
  }

  // ---- multi-select + bulk actions ----
  const selection = useRecordSelection();
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectedRecords = useMemo(
    () => records.filter((r) => selection.selected.has(r.uid)),
    [records, selection.selected]
  );
  const bulkStatuses = useMemo(
    () =>
      typeof selectedList === "number"
        ? (statusMap[selectedList] ?? []).map((s) => ({
            id: s.id,
            name: s.name,
            color: s.color,
            category: s.category,
          }))
        : [],
    [selectedList, statusMap]
  );
  async function runBulk(fn: () => Promise<void>): Promise<void> {
    if (bulkBusy || selectedRecords.length === 0) return;
    setBulkBusy(true);
    try {
      await fn();
      selection.clear();
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleDeleteRecord(r: TaskRecord): Promise<void> {
    if (!window.confirm(`Aufgabe „${r.title}" in den Papierkorb verschieben?`)) return;
    try {
      // deletePersonalTask refreshes the store, so the list updates itself.
      await deletePersonalTask(r.id);
      if (selectedTaskId === r.id) setSelectedTaskId(null);
      toastSuccess("Aufgabe gelöscht");
    } catch {
      toastError("Löschen fehlgeschlagen – bitte erneut versuchen");
    }
  }

  // Push query changes to the store.
  useEffect(() => {
    setPersonalTaskQuery({ listId: selectedList, includeDone });
  }, [selectedList, includeDone]);

  // Reset task-detail/selection/AI state whenever the active list changes
  // (the tree lives in the Sidebar now, so this is the only place left that
  // reacts to the URL-driven selection changing).
  useEffect(() => {
    setSelectedTaskId(null);
    selection.clear();
    setAiListSummary(null);
    setAiListSummary503(false);
    setAiListSummaryBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList]);

  // Deep-link open: ?task=<id> in the URL — Erinnerungs-Mail, Inbox-Link oder ein
  // geteilter Link ("Link kopieren"). Steht wie der Signal-Effekt unten BEWUSST
  // NACH dem Reset: als erster Effekt deklariert hat der Reset sein
  // setSelectedTaskId(null) beim Mount direkt wieder überschrieben, der Link kam
  // also nur in der Liste an, nicht in der Aufgabe. Die Reihenfolge hier ist
  // tragend — beim Verschieben mitnehmen.
  // Parsen + Entfernen steckt in parseAndStripTaskParam (store/deepLinks.ts),
  // geteilt mit Project.tsx.
  useEffect(() => {
    const taskId = parseAndStripTaskParam();
    if (taskId != null) setSelectedTaskId(taskId);
  }, [selectedList]);

  // Deep-link from the global search palette. Declared AFTER the reset effect
  // above so that on a fresh mount it runs last and isn't clobbered by the
  // reset's setSelectedTaskId(null). Reading .value subscribes the component,
  // so it fires even when already on /lists. Opens the detail regardless of
  // which list is selected (DetailPanel fetches the task by id).
  const pendingPersonalTaskId = openPersonalTaskSig.value;
  useEffect(() => {
    if (pendingPersonalTaskId != null) {
      setSelectedTaskId(pendingPersonalTaskId);
      openPersonalTaskSig.value = null;
    }
  }, [pendingPersonalTaskId]);

  const handleSummarizeList = async (): Promise<void> => {
    if (typeof selectedList !== "number") return;
    const reqId = ++summarizeListReqRef.current;
    const targetList = selectedList;
    setAiListSummaryBusy(true);
    setAiListSummary(null);
    setAiListSummary503(false);
    try {
      const res = await aiSummarizeList(targetList);
      if (summarizeListReqRef.current !== reqId) return;
      setAiListSummary(res.summary);
    } catch (err) {
      if (summarizeListReqRef.current !== reqId) return;
      if (err instanceof ApiError && err.status === 503) {
        setAiListSummary503(true);
      }
      // Non-503: swallowed (silentError: true in api fn; button re-enables)
    } finally {
      if (summarizeListReqRef.current === reqId) {
        setAiListSummaryBusy(false);
      }
    }
  };

  async function handleQuickAdd(): Promise<void> {
    const title = quickTitle.trim();
    if (!title) return;
    // "Alle Aufgaben" (selectedList "all") has no list of its own — the task
    // goes into the Standard-Liste / first personal list.
    if (selectedList === "all" && targetList === null) {
      toastError(NO_LIST_HINT);
      return;
    }
    setQuickTitle("");
    const list_id =
      typeof selectedList === "number" ? selectedList : (targetList?.id ?? undefined);
    try {
      await createPersonalTask({ title, list_id });
    } catch {
      // api() already toasts the error — restore the typed title so it isn't lost.
      setQuickTitle(title);
    }
  }

  const [showListAccess, setShowListAccess] = useState(false);

  const headerName =
    selectedList === "all"
      ? "Alle Aufgaben"
      : (lists.find((l) => l.id === selectedList)?.name ?? "Liste");

  // Redesign-Paket 2: der Seitenkopf wird nicht mehr selbst gebaut, sondern in
  // die EINE App-Kopfzeile geschrieben. Brotkrümel nur bei einer konkreten
  // Liste — „Alle Aufgaben" ist die Wurzel dieser Route, kein Kind einer
  // anderen Liste.
  const breadcrumb =
    typeof selectedList === "number"
      ? [{ label: "Aufgabenlisten", href: "/lists" }, { label: headerName }]
      : undefined;

  usePageHeader({
    title: headerName,
    breadcrumb,
    tabs: (
      <ViewTabs
        label="Ansicht"
        items={LIST_VIEW_TABS}
        active={viewState.view}
        onChange={(id) => updateViewState({ ...viewState, view: id as ViewKind })}
      />
    ),
    actions: (
      <>
        {selectedListObj && isShared(selectedListObj) && (
          <span class="rounded-full bg-[color:var(--color-secondary-container)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-secondary-container)]">
            {roleBadgeLabel(selectedListObj)}
          </span>
        )}
        {/* Bei einer GETEILTEN Liste ist „wer liest hier mit" nicht an der
            Liste ablesbar: der Zugriff kommt aus dem Space bzw. aus einer
            Abteilungs-Freigabe darauf. Bei einer persoenlichen Liste ist die
            Auskunft genauso wichtig — sie zeigt, dass eben NIEMAND sonst
            hereinsieht (ausser den Eigentuemern fremder Aufgaben darin). */}
        {typeof selectedList === "number" && (
          <Button variant="secondary" size="sm" icon={Eye} onClick={() => setShowListAccess(true)}>
            Wer hat Zugriff?
          </Button>
        )}
        {me.user?.ai_enabled === true && typeof selectedList === "number" && (
          <Button
            variant="secondary"
            size="sm"
            loading={aiListSummaryBusy}
            onClick={() => void handleSummarizeList()}
            aria-label="Liste mit KI zusammenfassen"
            title="Offene Aufgaben, Notizen und Schritte dieser Liste werden an den KI-Anbieter gesendet"
          >
            {aiListSummaryBusy ? "Zusammenfassung…" : "KI: Zusammenfassen"}
          </Button>
        )}
        {me.user?.ai_enabled === true && !me.user.is_external && (
          <Button
            variant="secondary"
            size="sm"
            icon={Sparkles}
            onClick={() => openTaskImport({ target: "personal" })}
            title="Eine Liste einfügen, die KI macht daraus Aufgaben und fragt vor dem Anlegen nach"
          >
            Liste importieren
          </Button>
        )}
        <label class="inline-flex min-h-[36px] items-center gap-1.5 text-xs text-[color:var(--color-on-surface-variant)] cursor-pointer">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => setIncludeDone((e.target as HTMLInputElement).checked)}
            class="size-4 accent-[color:var(--color-primary)] cursor-pointer"
          />
          Erledigte anzeigen
        </label>
      </>
    ),
  });

  return (
    <Layout>
      <PageShell width="default">
        {showListAccess && typeof selectedList === "number" && (
          <AccessOverviewDialog
            subject={{ kind: "list", id: selectedList }}
            onClose={() => setShowListAccess(false)}
          />
        )}

        {aiListSummary503 && (
          <p class="mb-3 text-xs italic text-[color:var(--color-on-surface-variant)]">
            KI ist derzeit deaktiviert.
          </p>
        )}
        {aiListSummary && !aiListSummary503 && (
          <div class="mb-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-3">
            <p
              class="text-sm text-[color:var(--color-on-surface)] whitespace-pre-wrap"
              data-testid="ai-list-summary-text"
            >
              {aiListSummary}
            </p>
            <div class="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(aiListSummary)}
                class="text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
              >
                Kopieren
              </button>
              <span class="text-xs italic text-[color:var(--color-on-surface-variant)] opacity-70">
                Offene Aufgaben, Notizen und Schritte wurden an den KI-Anbieter gesendet.
              </span>
            </div>
          </div>
        )}

        {writeAccess && (
          <div class="mb-3">
            <div class="flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2">
              <Plus size={16} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
              <input
                type="text"
                value={quickTitle}
                placeholder="Aufgabe hinzufügen"
                aria-label="Neue Aufgabe"
                disabled={missingTargetList}
                onInput={(e) => setQuickTitle((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleQuickAdd();
                }}
                class="w-full bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none disabled:cursor-not-allowed"
              />
            </div>
            {/* "Alle Aufgaben" is not a list — say where the task will land, or
                that a list has to exist first. */}
            {missingTargetList ? (
              <p
                class="mt-1.5 text-xs text-[color:var(--color-error)]"
                data-testid="lists-no-target-hint"
                role="status"
              >
                {NO_LIST_HINT}
              </p>
            ) : (
              targetList !== null && (
                <p
                  class="mt-1.5 text-xs text-[color:var(--color-on-surface-variant)]"
                  data-testid="lists-target-hint"
                >
                  Neue Aufgaben landen in „{targetList.name}". Die Standard-Liste lässt sich in
                  den Einstellungen ändern.
                </p>
              )
            )}
          </div>
        )}

        {/* P4: (board/calendar) group/sort controls — der Ansichtsumschalter
            selbst sitzt jetzt im Seitenkopf (usePageHeader tabs, oben). */}
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <GroupSortFilterBar
            state={viewState}
            onChange={updateViewState}
            groupByOptions={LIST_GROUP_OPTS}
            sortOptions={["manual", "due", "priority", "title"]}
          />
          <SavedViewsMenu scopeKey={scopeKey} current={viewState} onApply={updateViewState} />
        </div>

        {viewState.view === "board" ? (
          <TaskBoardView
            records={records}
            state={viewState}
            ctx={personalCtx}
            bucketOpts={bucketOpts}
            readOnly={!writeAccess}
            onOpenRecord={(r) => setSelectedTaskId(r.id)}
            emptyLabel="Keine Aufgaben"
          />
        ) : viewState.view === "calendar" ? (
          <TaskCalendarView
            records={records}
            state={viewState}
            ctx={personalCtx}
            readOnly={!writeAccess}
            onOpenRecord={(r) => setSelectedTaskId(r.id)}
          />
        ) : tasksLoading && tasks.length === 0 ? (
          <div class="rounded border border-[color:var(--color-outline-variant)] overflow-hidden bg-[color:var(--color-surface-container-lowest)]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                class="flex items-center gap-3 px-3 py-2.5 border-b border-[color:var(--color-outline-variant)]/60 min-h-[44px]"
              >
                <span class="juuplan-skeleton size-4 rounded-full shrink-0" />
                <span class="juuplan-skeleton h-3.5 rounded" style={{ width: `${40 + ((i * 13) % 35)}%` }} />
                <span class="juuplan-skeleton ml-auto size-6 rounded-full shrink-0" />
                <span class="juuplan-skeleton h-3.5 w-12 rounded shrink-0" />
                <span class="juuplan-skeleton h-5 w-20 rounded-full shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <TaskListView
            records={records}
            state={viewState}
            ctx={personalCtx}
            bucketOpts={bucketOpts}
            readOnly={!writeAccess}
            onOpenRecord={(r) => setSelectedTaskId(r.id)}
            onDeleteRecord={writeAccess ? handleDeleteRecord : undefined}
            onAddInGroup={writeAccess ? handleAddInGroup : undefined}
            onDisplayOrder={p3 ? setListOrder : undefined}
            selectedUids={selection.selected}
            onToggleSelect={writeAccess ? selection.toggle : undefined}
            emptyLabel="Keine Aufgaben"
          />
        )}
      </PageShell>

      {p3Record && (
        <DetailSheet onClose={() => setSelectedTaskId(null)}>
          <TaskDetailPanel
            record={p3Record}
            ctx={p3Ctx}
            onClose={() => setSelectedTaskId(null)}
            onNavigate={(dir) => {
              const order =
                viewState.view === "list" && listOrder !== null
                  ? listOrder
                  : displayOrder(computeView(records, viewState, bucketOpts), new Set(), viewState.groupBy === "none");
              const next = neighbourUid(order, p3Record.uid, dir);
              const rec = records.find((r) => r.uid === next);
              if (rec) setSelectedTaskId(rec.id);
            }}
            onOpenFull={() => setP3Full(true)}
            onChanged={() => {
              void refreshPersonalTasks();
              if (!p3InList && selectedTaskId !== null) loadP3Extra(selectedTaskId);
            }}
          />
        </DetailSheet>
      )}

      {showClassicDetail && (
        <DetailPanel
          taskId={selectedTaskId}
          lists={lists}
          readOnly={!writeAccess}
          onClose={() => setSelectedTaskId(null)}
        />
      )}

      {writeAccess && (
        <BulkActionToolbar
          count={selection.size}
          busy={bulkBusy}
          statuses={bulkStatuses}
          onSetDone={() =>
            void runBulk(() => personalCtx.patchMany!(selectedRecords, { done: true }))
          }
          onSetPriority={(p) =>
            void runBulk(() => personalCtx.patchMany!(selectedRecords, { priority: p }))
          }
          onSetStatus={(statusId) =>
            void runBulk(() => personalCtx.patchMany!(selectedRecords, { statusId }))
          }
          onDelete={() =>
            void runBulk(() => personalCtx.deleteMany!(selectedRecords))
          }
          onClear={() => selection.clear()}
        />
      )}
    </Layout>
  );
}

// ---------- detail panel ----------

export function DetailPanel(props: {
  taskId: number;
  lists: TaskList[];
  readOnly: boolean;
  onClose: () => void;
}): preact.JSX.Element {
  const { taskId, lists, readOnly: readOnlyProp, onClose } = props;
  const [, setLocation] = useLocation();
  const [detail, setDetail] = useState<PersonalTaskDetail | null>(null);
  // Grabstein: `detail.merged_into_personal_task_id` gesetzt = diese Aufgabe
  // wurde zusammengeführt und ist geschlossen. `readOnly` fasst Viewer-Rolle
  // UND Grabstein zusammen, damit jede bestehende Gating-Stelle unten (Titel,
  // Felder, Schritte, …) automatisch mitsperrt.
  const isMerged = detail?.merged_into_personal_task_id != null;
  const readOnly = readOnlyProp || isMerged;
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [addingToMyDay, setAddingToMyDay] = useState(false);
  // Getrennt von `detail === null`, sonst ist „lädt noch" nicht von
  // „Laden fehlgeschlagen" zu unterscheiden.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepTitle, setStepTitle] = useState("");
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [editStepValue, setEditStepValue] = useState("");
  // Aufgabe-verknüpfen-Modus (Referenz-Schritt): Suchfeld statt Titel-Eingabe.
  const [linkMode, setLinkMode] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState<
    { type: "task" | "personal_task"; id: number; title: string; subtitle?: string }[] | null
  >(null);
  const linkSearchSeq = useRef(0);
  const [reminderAt, setReminderAt] = useState("");
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [spaceMembers, setSpaceMembers] = useState<SpaceMember[]>([]);

  // Favorite star + the current list's configured statuses (for the rail selector).
  const favorites = useFavorites();
  const isFav = favorites.personal_task.has(taskId);
  const listStatuses = useListStatuses(detail?.list_id ?? null);

  // The assignee ("Zuständig") picker is scoped to the space of the TASK'S OWN
  // list — not the sidebar selection. Otherwise opening a shared task from
  // "Alle Aufgaben" hides the field entirely, and moving a task
  // between lists via the Liste dropdown would show the wrong space's members.
  // Falls back to no picker for personal / no-space tasks.
  const taskList = detail ? lists.find((l) => l.id === detail.list_id) : undefined;
  const isSpaceList = taskList?.scope === "space";
  const spaceId = taskList?.space_id ?? undefined;

  // Zuständige der geöffneten Aufgabe (#854). assignee_id ist nur noch der
  // primäre Fallback für Payloads eines älteren Servers.
  const detailAssigneeIds = useMemo<number[]>(
    () => personalAssigneeIds(detail),
    [detail]
  );

  useEffect(() => {
    if (isSpaceList && spaceId) {
      // Kein sichtbarer Fehlerzustand hier absichtlich: ein Fehlschlag liesse
      // nur die Kandidatenliste im Zuständige-Picker leer statt das ganze
      // Panel zu blockieren. `fetchSpaceMembers` selbst ist `silentError`.
      void fetchSpaceMembers(spaceId)
        .then(setSpaceMembers)
        .catch(() => setSpaceMembers([]));
    } else {
      setSpaceMembers([]);
    }
  }, [isSpaceList, spaceId]);

  async function reload(): Promise<void> {
    setDetail(await fetchPersonalTaskDetail(taskId));
  }

  // Laden der Aufgabe. GETs werden von api() bewusst nicht getoastet — ohne
  // eigenen Fehlerzustand bliebe das Panel bei einem 404/403 endlos auf „Lädt…".
  // Seit „Link kopieren" ist das ein realer Fall: ein geteilter Link kann auf
  // eine gelöschte Aufgabe oder eine ohne Zugriff zeigen.
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError(null);
    void fetchPersonalTaskDetail(taskId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const notFound = err instanceof ApiError && (err.status === 403 || err.status === 404);
        setLoadError(
          notFound
            ? "Aufgabe nicht gefunden oder kein Zugriff."
            : "Aufgabe konnte nicht geladen werden."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  async function patch(p: Parameters<typeof updatePersonalTask>[1]): Promise<void> {
    if (readOnly) return;
    await updatePersonalTask(taskId, p);
    await reload();
  }

  async function handleAddToMyDay(): Promise<void> {
    setAddingToMyDay(true);
    try {
      await addToMyDay({ personal_task_id: taskId });
      toastSuccess(`„${detail?.title}" zu „Mein Tag" hinzugefügt`);
    } catch {
      // addToMyDay -> api() hat den Fehler bereits getoastet.
    } finally {
      setAddingToMyDay(false);
    }
  }

  // Nach erfolgreichem Zusammenführen: Dialog zu und aufs ZIEL umschalten —
  // openPersonalTaskSig.value öffnet es im selben Panel (Muster wie beim
  // Übergeordnete-Aufgabe-Link oben). mergePersonalTask() in MergeTaskDialog
  // hat die Listen bereits neu geladen.
  function onMerged(target: { id: number }): void {
    setShowMergeDialog(false);
    openPersonalTaskSig.value = target.id;
  }

  async function handleAddStep(): Promise<void> {
    if (readOnly) return;
    const title = stepTitle.trim();
    if (!title) return;
    setStepTitle("");
    try {
      await addStep(taskId, title);
      await reload();
    } catch {
      // api() toasts the error — keep the typed title.
      setStepTitle(title);
    }
  }

  // Verknüpfungs-Suche (Referenz-Schritt): debounced /api/search über Projekt-
  // + persönliche Aufgaben; bereits verknüpfte Ziele und diese Aufgabe selbst
  // werden ausgefiltert. `status=open` blendet erledigte/abgebrochene Aufgaben
  // aus — ein abgeschlossenes Ziel ist als Schritt sinnlos. Spiegelt
  // RecordStepList.
  useEffect(() => {
    if (!linkMode) return;
    const q = linkQuery.trim();
    if (q.length < 2) {
      setLinkResults(null);
      return;
    }
    const seq = ++linkSearchSeq.current;
    const t = setTimeout(() => {
      void api<{ results: { type: string; id: number; title: string; subtitle?: string }[] }>(
        `/api/search?q=${encodeURIComponent(q)}&type=task,personal_task&status=open`
      ).then(
        (data) => {
          if (seq !== linkSearchSeq.current) return;
          const linkedIds = new Set(
            (detail?.steps ?? [])
              .filter((s) => s.linked_info)
              .map((s) => `${s.linked_info!.type}:${s.linked_info!.id}`)
          );
          setLinkResults(
            data.results
              .filter(
                (r): r is { type: "task" | "personal_task"; id: number; title: string; subtitle?: string } =>
                  r.type === "task" || r.type === "personal_task"
              )
              .filter((r) => !(r.type === "personal_task" && r.id === taskId))
              .filter((r) => !linkedIds.has(`${r.type}:${r.id}`))
              .slice(0, 8)
          );
        },
        () => {
          // Absichtlich still: Tippen-als-Suche ist ein optionaler Abruf —
          // ein Fehlschlag zeigt "keine Treffer" statt eines Toasts pro
          // Tastendruck.
          if (seq === linkSearchSeq.current) setLinkResults([]);
        }
      );
    }, 250);
    return () => clearTimeout(t);
  }, [linkMode, linkQuery, detail, taskId]);

  async function handleLink(result: { type: "task" | "personal_task"; id: number }): Promise<void> {
    try {
      await addLinkedStep(
        taskId,
        result.type === "task"
          ? { linked_task_id: result.id }
          : { linked_personal_task_id: result.id }
      );
      setLinkQuery("");
      setLinkResults(null);
      setLinkMode(false);
      await reload();
    } catch {
      // api() toasts the error.
    }
  }

  async function commitStepEdit(step: PersonalTaskStep): Promise<void> {
    const trimmed = editStepValue.trim();
    setEditingStepId(null);
    if (!trimmed || trimmed === step.title) return;
    await updateStep(step.id, { title: trimmed });
    await reload();
  }

  async function handleRecurrence(value: string): Promise<void> {
    if (readOnly) return;
    if (value === "none") {
      await clearRecurrence(taskId);
    } else {
      const interval = detail?.recurrence?.interval_count ?? 1;
      await setRecurrence(taskId, value as RecurrenceRule, interval);
    }
    await reload();
  }

  async function handleInterval(n: number): Promise<void> {
    if (readOnly || !detail?.recurrence) return;
    // Guard against a cleared/invalid number field (NaN would reach the API).
    if (!Number.isFinite(n)) return;
    await setRecurrence(taskId, detail.recurrence.rule, Math.max(1, Math.round(n)));
    await reload();
  }

  async function handleReminder(): Promise<void> {
    if (readOnly || !reminderAt) return;
    await createReminder({
      personal_task_id: taskId,
      remind_at: new Date(reminderAt).toISOString(),
    });
    toastSuccess("Erinnerung gesetzt.");
    setReminderAt("");
  }

  const me = useMe();

  const doneSteps = detail?.steps.filter((s) => s.is_done).length ?? 0;
  const totalSteps = detail?.steps.length ?? 0;

  // AI — suggest steps
  const [suggestStepsBusy, setSuggestStepsBusy] = useState(false);
  const [suggestStepsItems, setSuggestStepsItems] = useState<string[]>([]);
  const [suggestSteps503, setSuggestSteps503] = useState(false);

  // AI — summarize personal task
  const [aiPersonalTaskSummaryBusy, setAiPersonalTaskSummaryBusy] = useState(false);
  const [aiPersonalTaskSummary, setAiPersonalTaskSummary] = useState<string | null>(null);
  const [aiPersonalTaskSummary503, setAiPersonalTaskSummary503] = useState(false);

  // Ref that always holds the current taskId so in-flight responses can detect
  // when the panel has moved to a different task.
  const taskIdRef = useRef(taskId);
  useEffect(() => {
    taskIdRef.current = taskId;
  }, [taskId]);

  // Double-adopt guard: track which step titles are currently being adopted.
  const adoptingRef = useRef<Set<string>>(new Set());
  const [adoptingTitles, setAdoptingTitles] = useState<Set<string>>(new Set());

  // Reset AI state whenever the viewed task changes.
  useEffect(() => {
    setSuggestStepsBusy(false);
    setSuggestStepsItems([]);
    setSuggestSteps503(false);
    setAiPersonalTaskSummaryBusy(false);
    setAiPersonalTaskSummary(null);
    setAiPersonalTaskSummary503(false);
  }, [taskId]);

  const handleSuggestSteps = async (): Promise<void> => {
    const tid = taskId;
    setSuggestStepsBusy(true);
    setSuggestStepsItems([]);
    setSuggestSteps503(false);
    try {
      const res = await aiSuggestSteps(tid);
      if (taskIdRef.current !== tid) return;
      setSuggestStepsItems(res.suggestions);
    } catch (err) {
      if (taskIdRef.current !== tid) return;
      if (err instanceof ApiError && err.status === 503) {
        setSuggestSteps503(true);
      }
      // Non-503: auto-toasted by api()
    } finally {
      if (taskIdRef.current === tid) {
        setSuggestStepsBusy(false);
      }
    }
  };

  const handleAdoptStep = async (title: string): Promise<void> => {
    if (adoptingRef.current.has(title)) return;
    const tid = taskId;
    adoptingRef.current.add(title);
    setAdoptingTitles(new Set(adoptingRef.current));
    try {
      await addStep(tid, title);
      if (taskIdRef.current === tid) {
        setSuggestStepsItems((prev) => prev.filter((s) => s !== title));
        await reload();
      }
    } finally {
      adoptingRef.current.delete(title);
      setAdoptingTitles(new Set(adoptingRef.current));
    }
  };

  const handleSummarizePersonalTask = async (): Promise<void> => {
    const tid = taskId;
    setAiPersonalTaskSummaryBusy(true);
    setAiPersonalTaskSummary(null);
    setAiPersonalTaskSummary503(false);
    try {
      const res = await aiSummarizePersonalTask(tid);
      if (taskIdRef.current !== tid) return;
      setAiPersonalTaskSummary(res.summary);
    } catch (err) {
      if (taskIdRef.current !== tid) return;
      if (err instanceof ApiError && err.status === 503) {
        setAiPersonalTaskSummary503(true);
      }
      // Non-503: swallowed (silentError: true in api fn)
    } finally {
      if (taskIdRef.current === tid) {
        setAiPersonalTaskSummaryBusy(false);
      }
    }
  };

  // Field label shown above each property in the right rail.
  const fieldLabel = (icon: preact.JSX.Element, text: string): preact.JSX.Element => (
    <span class="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
      {icon}
      {text}
    </span>
  );
  // Teilbarer Direktlink auf diese Aufgabe: Empfänger landen über den
  // `?task=`-Parser oben direkt im Detail, nicht nur in der Liste. Es zählt die
  // Liste der Aufgabe selbst (detail.list_id) — die gerade gewählte Ansicht kann
  // eine Pseudo-Liste ("all"/null) sein und ergäbe keinen stabilen Link. Ohne
  // Liste (Inbox-Aufgabe) greift "/lists", da die Route `/lists/:id?` heisst.
  const copyDeepLink = async (): Promise<void> => {
    const base = detail?.list_id != null ? `/lists/${detail.list_id}` : "/lists";
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${base}?task=${taskId}`
      );
      toastSuccess("Link kopiert");
    } catch {
      toastError("Link konnte nicht kopiert werden.");
    }
  };

  // text-base on mobile: <16px inputs trigger the iOS focus-zoom.
  const railInput =
    "w-full rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]";

  return (
    <Modal title={detail?.title || "Aufgabe"} size="2xl" hideHeader onClose={onClose}>
      {loadError ? (
        // Eigener Schließen-Button: die Kopfzeile mit dem regulären steckt im
        // detail-Zweig, ohne ihn säße man hier fest.
        <div class="flex items-start justify-between gap-3">
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">{loadError}</p>
          <button
            type="button"
            onClick={onClose}
            class="shrink-0 rounded-md p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label="Schließen"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
      ) : !detail ? (
        <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>
      ) : (
        <div class="flex flex-col min-h-0">
          {/* ---- Grabstein: die Aufgabe wurde in eine andere zusammengeführt
              — alle Inhalte hängen jetzt am Ziel, hier bleibt nur der Verweis. ---- */}
          {isMerged && (
            <div class="mb-3 flex items-center gap-2 rounded-md border border-[color:var(--color-warning,#A16207)]/30 bg-[color:var(--color-warning,#A16207)]/8 px-3 py-2">
              <AlertTriangle
                size={16}
                class="shrink-0 text-[color:var(--color-warning,#A16207)]"
                aria-hidden="true"
              />
              <span class="flex-1 text-sm text-[color:var(--color-on-surface)]">
                Diese Aufgabe wurde in Aufgabe #{detail.merged_into_personal_task_id}{" "}
                zusammengeführt.
              </span>
              <button
                type="button"
                onClick={() => {
                  openPersonalTaskSig.value = detail.merged_into_personal_task_id as number;
                }}
                class="shrink-0 rounded-md border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
              >
                Ziel öffnen
              </button>
            </div>
          )}
          {/* ---- Back-link: shown when the open task is itself a step ---- */}
          {detail.parent_id != null && (
            <button
              type="button"
              onClick={() => {
                openPersonalTaskSig.value = detail.parent_id ?? null;
              }}
              class="mb-2 inline-flex w-fit items-center gap-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
            >
              ↑ Übergeordnete Aufgabe
            </button>
          )}
          {/* ---- Header: big editable title + close ---- */}
          <div class="flex flex-wrap items-start gap-x-3 gap-y-1 pb-4 border-b border-[color:var(--color-outline-variant)]">
            <div class="flex-1 min-w-[min(100%,14rem)]">
              <TitleField
                value={detail.title}
                readOnly={readOnly}
                placeholder="Aufgabentitel"
                label="Aufgabentitel"
                maxLength={500}
                onValue={readOnly ? undefined : (v) => setDetail({ ...detail, title: v })}
                onBlur={readOnly ? undefined : (v) => void patch({ title: v })}
                class={`rounded-md border border-transparent bg-transparent px-2 py-1 -ml-2 text-xl leading-7 sm:text-2xl sm:leading-8 font-bold text-balance text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus:border-[color:var(--color-outline-variant)] focus:bg-[color:var(--color-surface-container-lowest)] ${readOnly ? "cursor-default" : "hover:bg-[color:var(--color-surface-container-low)]"}`}
              />
              {readOnlyProp && (
                <span class="ml-0.5 mt-1 inline-block rounded-full bg-[color:var(--color-secondary-container)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-secondary-container)]">
                  Nur Lesen
                </span>
              )}
            </div>
            {/* Aktionen als EIN Block, der auf schmalen Fenstern gemeinsam in
                die naechste Zeile bricht, statt den Titel zu zerquetschen. */}
            <div class="ml-auto flex shrink-0 items-start gap-0.5">
            {!readOnly && !detail.is_done && (
              <button
                type="button"
                onClick={() => void handleAddToMyDay()}
                disabled={addingToMyDay}
                class="shrink-0 rounded-md p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-50"
                aria-label={'Zu „Mein Tag" hinzufügen'}
                title={'Zu „Mein Tag" hinzufügen'}
              >
                <Sun size={20} aria-hidden="true" />
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => setShowMergeDialog(true)}
                class="shrink-0 rounded-md p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Zusammenführen…"
                title="In eine andere Aufgabe zusammenführen — diese Aufgabe wird dabei geschlossen"
              >
                <GitMerge size={20} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={() => void copyDeepLink()}
              class="shrink-0 rounded-md p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              aria-label="Link zur Aufgabe kopieren"
              title="Link kopieren"
            >
              <Link2 size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() =>
                void toggleFavorite("personal_task", taskId).catch(() => {
                  /* toggleFavorite() hat schon gemeldet */
                })
              }
              class={`shrink-0 rounded-md p-2 hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                isFav
                  ? "text-[color:var(--color-warning,#F59E0B)]"
                  : "text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
              }`}
              aria-label={isFav ? "Favorit entfernen" : "Als Favorit markieren"}
              aria-pressed={isFav}
              title={isFav ? "Favorit entfernen" : "Als Favorit markieren"}
            >
              <Star size={20} aria-hidden="true" fill={isFav ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={onClose}
              class="shrink-0 rounded-md p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              aria-label="Schließen"
            >
              <X size={20} aria-hidden="true" />
            </button>
            </div>
          </div>

          {/* ---- AI: personal-task summary (gated: ai_enabled only, no readOnly gate) ---- */}
          {me.user?.ai_enabled === true && (
            <div class="mt-3 flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  disabled={aiPersonalTaskSummaryBusy}
                  onClick={() => void handleSummarizePersonalTask()}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50 transition-colors"
                >
                  {aiPersonalTaskSummaryBusy ? "Zusammenfassung…" : "KI: Zusammenfassen"}
                </button>
              </div>
              {aiPersonalTaskSummary503 && (
                <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                  KI ist derzeit deaktiviert.
                </p>
              )}
              {aiPersonalTaskSummary && !aiPersonalTaskSummary503 && (
                <div
                  data-testid="ai-personal-task-summary-panel"
                  class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-3"
                >
                  <p
                    data-testid="ai-task-summary-text"
                    class="text-sm text-[color:var(--color-on-surface)] whitespace-pre-wrap"
                  >
                    {aiPersonalTaskSummary}
                  </p>
                  <div class="mt-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard.writeText(aiPersonalTaskSummary)}
                      class="text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
                    >
                      Kopieren
                    </button>
                    <span class="text-xs italic text-[color:var(--color-on-surface-variant)] opacity-70">
                      Titel, Notizen, Schritte und Kommentare wurden an den KI-Anbieter gesendet.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Body: main content (left) + properties rail (right) ---- */}
          <div class="grid gap-6 pt-4 md:grid-cols-[1fr_260px]">
            {/* ---- Main column ---- */}
            <div class="flex flex-col gap-6 min-w-0">
              {/* Notes — rich-text (bold/italic/lists/emoji/links) */}
              <div class="flex flex-col gap-1.5">
                <span class="text-xs font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
                  Beschreibung
                </span>
                <RichTextEditor
                  value={detail.notes}
                  docKey={taskId}
                  readOnly={readOnly}
                  placeholder="Beschreibung hinzufügen…"
                  onCommit={(html) => void patch({ notes: html })}
                />
              </div>

              {/* Steps / subtasks — hidden when this task is itself a step
                  (the server rejects steps-on-steps). */}
              {detail.parent_id == null && (
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <span class="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
                    <ListChecks size={14} aria-hidden="true" /> Schritte
                  </span>
                  <span class="inline-flex items-center gap-3">
                    {/* #826: der Auslöser gehört sichtbar hierher, nicht nur in
                        die eingeklappte Schritte-Liste der Übersicht. Beschriftet
                        statt reines Icon — er war sonst schlicht nicht auffindbar.
                        Nur wenn es überhaupt etwas zu übernehmen gibt. */}
                    {!readOnly && doneSteps < totalSteps && (
                      <button
                        type="button"
                        onClick={() => {
                          carryOverStepsSig.value = detail.id;
                        }}
                        class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                        title="Die offenen Punkte in eine neue Aufgabe übernehmen und diese hier abschliessen"
                      >
                        <ArrowRightToLine size={14} aria-hidden="true" />
                        Offene übernehmen
                      </button>
                    )}
                    {totalSteps > 0 && (
                      <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                        {doneSteps}/{totalSteps} erledigt
                      </span>
                    )}
                  </span>
                </div>
                {totalSteps > 0 && (
                  <div
                    class="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-surface-container)]"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={totalSteps}
                    aria-valuenow={doneSteps}
                    aria-label={`${doneSteps} von ${totalSteps} Schritten erledigt`}
                  >
                    <div
                      class={`h-full rounded-full transition-all ${
                        doneSteps === totalSteps
                          ? "bg-[color:var(--color-status-done)]"
                          : "bg-[color:var(--color-primary)]"
                      }`}
                      style={{ width: `${Math.round((doneSteps / totalSteps) * 100)}%` }}
                    />
                  </div>
                )}
                <ul class="flex flex-col">
                  {detail.steps.map((s) => {
                    const stepLinked = s.linked_info ?? null;
                    const stepToggleLocked =
                      readOnly ||
                      (stepLinked !== null &&
                        (stepLinked.type === "task" || !stepLinked.access));
                    return (
                    <li key={s.id} class="group flex items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-[color:var(--color-surface-container-low)]">
                      <button
                        type="button"
                        disabled={stepToggleLocked}
                        onClick={stepToggleLocked ? undefined : async () => {
                          await updateStep(s.id, { is_done: !s.is_done });
                          await reload();
                        }}
                        class={`shrink-0 inline-flex size-7 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${stepToggleLocked ? "cursor-default opacity-60" : ""}`}
                        aria-label={s.is_done ? `„${s.title}" wieder öffnen` : `„${s.title}" erledigen`}
                        aria-pressed={s.is_done}
                        title={
                          stepLinked?.type === "task"
                            ? "Status wird von der Projektaufgabe übernommen"
                            : undefined
                        }
                      >
                        {s.is_done ? (
                          <span class="inline-flex size-[18px] items-center justify-center rounded-full bg-[color:var(--color-status-done)] text-[color:var(--color-on-primary)]">
                            <Check size={14} aria-hidden="true" />
                          </span>
                        ) : (
                          <Circle size={20} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
                        )}
                      </button>
                      {/* Verknüpfungs-Marker für Referenz-Schritte */}
                      {stepLinked && (
                        <Link2
                          size={14}
                          class="shrink-0 text-[color:var(--color-primary)]"
                          aria-label={
                            stepLinked.type === "task"
                              ? "Verknüpfte Projektaufgabe"
                              : "Verknüpfte persönliche Aufgabe"
                          }
                        />
                      )}
                      {/* priority marker — read-only, only shown when non-default */}
                      {s.priority !== "medium" && (
                        <Flag
                          size={14}
                          class="shrink-0"
                          fill="currentColor"
                          style={{ color: TASK_PRIORITY_VAR[s.priority] }}
                          aria-label={`Priorität: ${TASK_PRIORITY_LABEL[s.priority]}`}
                        />
                      )}
                      {/* assignee marker — read-only; seit #854 mehrere möglich */}
                      {personalAssigneeIds(s).length > 0 && (
                        <AvatarStack userIds={personalAssigneeIds(s)} />
                      )}
                      {editingStepId === s.id ? (
                        <input
                          type="text"
                          value={editStepValue}
                          autoFocus
                          onInput={(e) => setEditStepValue((e.target as HTMLInputElement).value)}
                          onBlur={() => void commitStepEdit(s)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              setEditingStepId(null);
                            }
                          }}
                          maxLength={500}
                          aria-label={`„${s.title}" umbenennen`}
                          class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-0.5 text-base sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                        />
                      ) : (
                        // <button> statt <span>: Enter/Leertaste oeffnet den
                        // Schritt, F2 benennt um — vorher nur per Maus.
                        <button
                          type="button"
                          aria-disabled={stepLinked && !stepLinked.access ? "true" : undefined}
                          onKeyDown={readOnly || stepLinked ? undefined : (e) => {
                            if (e.key !== "F2") return;
                            e.preventDefault();
                            setEditingStepId(s.id);
                            setEditStepValue(s.title);
                          }}
                          onClick={() => {
                            if (stepLinked) {
                              if (!stepLinked.access) return;
                              if (stepLinked.type === "task") {
                                // Signal + Navigation (wie Command-Palette).
                                openProjectTaskSig.value = stepLinked.id;
                                if (stepLinked.project_id != null) {
                                  setLocation(`/p/${stepLinked.project_id}`);
                                }
                              } else {
                                openPersonalTaskSig.value = stepLinked.id;
                              }
                              return;
                            }
                            openPersonalTaskSig.value = s.id;
                          }}
                          onDblClick={readOnly || stepLinked ? undefined : () => {
                            setEditingStepId(s.id);
                            setEditStepValue(s.title);
                          }}
                          title={
                            stepLinked
                              ? stepLinked.access
                                ? "Verknüpfte Aufgabe öffnen"
                                : undefined
                              : readOnly
                                ? "Öffnen"
                                : "Klick zum Öffnen, Doppelklick zum Bearbeiten"
                          }
                          class={`flex-1 min-w-0 rounded text-left text-sm [overflow-wrap:anywhere] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                            stepLinked && !stepLinked.access ? "cursor-default" : "cursor-pointer"
                          } ${
                            s.is_done ? "line-through opacity-60" : "text-[color:var(--color-on-surface)]"
                          }`}
                        >
                          {s.title}
                          {stepLinked?.trashed && (
                            <span class="ml-1.5 text-micro italic text-[color:var(--color-on-surface-variant)]">
                              (gelöscht)
                            </span>
                          )}
                          {stepLinked && !stepLinked.access && !stepLinked.trashed && (
                            <span class="ml-1.5 text-micro italic text-[color:var(--color-on-surface-variant)]">
                              (kein Zugriff)
                            </span>
                          )}
                        </button>
                      )}
                      {/* Live-Status des verknüpften Ziels */}
                      {stepLinked?.access && s.status_info && (
                        <span
                          class="shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-label font-medium"
                          style={softPillStyle(s.status_info.color)}
                          title={`Status: ${s.status_info.name}`}
                        >
                          <span
                            class="inline-block size-1.5 rounded-full"
                            style={{ backgroundColor: s.status_info.color }}
                            aria-hidden="true"
                          />
                          {s.status_info.name}
                        </span>
                      )}
                      {/* due date — inline editable, mirrors the row/RecordRow pattern */}
                      {!stepLinked && (s.due_date || !readOnly) && (
                        <span class="relative inline-flex items-center shrink-0" title="Fälligkeit">
                          {s.due_date ? (
                            <span
                              class={`text-micro tabular-nums ${
                                !s.is_done && s.due_date < localTodayIso()
                                  ? "text-[color:var(--color-prio-urgent)] font-semibold"
                                  : "text-[color:var(--color-on-surface-variant)]"
                              }`}
                            >
                              {formatDateShort(s.due_date)}
                            </span>
                          ) : (
                            <CalendarPlus
                              size={16}
                              class="text-[color:var(--color-outline)] opacity-100 md:opacity-40 md:group-hover:opacity-100 transition-opacity"
                              aria-hidden="true"
                            />
                          )}
                          {!readOnly && (
                            <input
                              type="date"
                              value={s.due_date ?? ""}
                              onClick={(e) => {
                                const el = e.currentTarget as HTMLInputElement & {
                                  showPicker?: () => void;
                                };
                                try {
                                  el.showPicker?.();
                                } catch {
                                  /* native input still opens on focus/click */
                                }
                              }}
                              onChange={async (e) => {
                                const v = (e.currentTarget as HTMLInputElement).value;
                                await updateStep(s.id, { due_date: v || null });
                                await reload();
                              }}
                              class="absolute inset-0 opacity-0 cursor-pointer"
                              aria-label={`Fälligkeit von „${s.title}"`}
                            />
                          )}
                        </span>
                      )}
                      {/* In eine eigene Aufgabe umwandeln — nur für echte
                          Schritte: ein Referenz-Schritt zeigt bereits auf eine
                          Aufgabe. Der Punkt verlässt danach diese Checkliste. */}
                      {!readOnly && !stepLinked && (
                        <button
                          type="button"
                          onClick={async () => {
                            await promoteStep(s.id);
                            await reload();
                            toastSuccess(`„${s.title}" ist jetzt eine eigene Aufgabe`);
                          }}
                          class="shrink-0 inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-primary)] focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label={`Schritt „${s.title}" in eine Aufgabe umwandeln`}
                          title="In eine eigene Aufgabe umwandeln (verlässt diese Checkliste)"
                        >
                          <ArrowUpRight size={14} aria-hidden="true" />
                        </button>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={async () => {
                            await deleteStep(s.id);
                            await reload();
                          }}
                          class="shrink-0 inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-[color:var(--color-error)]/10 hover:text-[color:var(--color-error)] focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)]"
                          aria-label={
                            stepLinked
                              ? `Verknüpfung „${s.title}" entfernen`
                              : `Schritt „${s.title}" löschen`
                          }
                          title={stepLinked ? "Verknüpfung entfernen (Aufgabe bleibt bestehen)" : "Schritt löschen"}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                    );
                  })}
                </ul>
                {!readOnly && !linkMode && (
                  <div class="flex items-center gap-2 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5">
                    <Plus size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
                    <input
                      type="text"
                      value={stepTitle}
                      placeholder="Schritt hinzufügen"
                      aria-label="Neuer Schritt"
                      onInput={(e) => setStepTitle((e.target as HTMLInputElement).value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleAddStep();
                      }}
                      class="w-full bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setLinkMode(true)}
                      class="shrink-0 inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                      aria-label="Bestehende Aufgabe verknüpfen"
                      title="Bestehende Aufgabe als Schritt verknüpfen"
                    >
                      <Link2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                )}
                {!readOnly && linkMode && (
                  <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-2 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5">
                      <Link2 size={14} class="shrink-0 text-[color:var(--color-primary)]" aria-hidden="true" />
                      <input
                        type="text"
                        value={linkQuery}
                        autoFocus
                        placeholder="Aufgabe suchen und verknüpfen…"
                        aria-label="Aufgabe suchen und als Schritt verknüpfen"
                        maxLength={100}
                        onInput={(e) => setLinkQuery((e.target as HTMLInputElement).value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            setLinkMode(false);
                            setLinkQuery("");
                            setLinkResults(null);
                          }
                        }}
                        class="w-full bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setLinkMode(false);
                          setLinkQuery("");
                          setLinkResults(null);
                        }}
                        class="shrink-0 text-micro text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none"
                        aria-label="Verknüpfen abbrechen"
                      >
                        Abbrechen
                      </button>
                    </div>
                    {linkResults !== null && (
                      <ul class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-1 overflow-hidden">
                        {linkResults.length === 0 ? (
                          <li class="px-2.5 py-1.5 text-xs italic text-[color:var(--color-on-surface-variant)]">
                            Keine passende Aufgabe gefunden
                          </li>
                        ) : (
                          linkResults.map((r) => (
                            <li key={`${r.type}:${r.id}`}>
                              <button
                                type="button"
                                onClick={() => void handleLink(r)}
                                class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)]"
                              >
                                <Link2 size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
                                <span class="min-w-0 truncate text-[color:var(--color-on-surface)]">{r.title}</span>
                                <span class="ml-auto shrink-0 text-label text-[color:var(--color-on-surface-variant)]">
                                  {r.type === "task" ? (r.subtitle ?? "Projektaufgabe") : "Persönliche Aufgabe"}
                                </span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                )}
                {/* ---- AI: suggest steps (gated: ai_enabled && !readOnly) ---- */}
                {me.user?.ai_enabled === true && !readOnly && (
                  <div class="flex flex-col gap-2">
                    <button
                      type="button"
                      disabled={suggestStepsBusy}
                      onClick={() => void handleSuggestSteps()}
                      class="self-start rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50 transition-colors"
                    >
                      {suggestStepsBusy ? "Schlage vor…" : "KI: Schritte vorschlagen"}
                    </button>
                    {suggestSteps503 && (
                      <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                        KI ist derzeit deaktiviert.
                      </p>
                    )}
                    {suggestStepsItems.length > 0 && (
                      <ul class="flex flex-col gap-1">
                        {suggestStepsItems.map((s) => (
                          <li
                            key={s}
                            class="flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                          >
                            <span class="flex-1 truncate">{s}</span>
                            <button
                              type="button"
                              onClick={() => void handleAdoptStep(s)}
                              disabled={adoptingTitles.has(s)}
                              aria-label={`Schritt übernehmen: ${s}`}
                              class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-label font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50 transition-colors"
                            >
                              Übernehmen
                            </button>
                            <button
                              type="button"
                              onClick={() => setSuggestStepsItems((prev) => prev.filter((x) => x !== s))}
                              aria-label="Vorschlag verwerfen"
                              class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Notes / comments — neueste zuerst, editierbar vom Ersteller */}
              <NotesSection personalTaskId={taskId} readOnly={readOnly} />
            </div>

            {/* ---- Properties rail ---- */}
            <aside class="flex flex-col gap-4 md:border-l md:border-[color:var(--color-outline-variant)] md:pl-6">
              <label class="flex flex-col gap-1.5">
                {fieldLabel(<CalendarDays size={14} aria-hidden="true" />, "Fällig")}
                <input
                  type="date"
                  value={detail.due_date ?? ""}
                  disabled={readOnly}
                  onInput={readOnly ? undefined : (e) => void patch({ due_date: (e.target as HTMLInputElement).value || null })}
                  class={`${railInput} ${readOnly ? "opacity-70 cursor-default" : ""}`}
                />
              </label>

              <label class="flex flex-col gap-1.5">
                {fieldLabel(<Flag size={14} aria-hidden="true" />, "Priorität")}
                <select
                  value={detail.priority}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => void patch({ priority: (e.target as HTMLSelectElement).value })}
                  class={`${railInput} ${readOnly ? "opacity-70 cursor-default" : ""}`}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <label class="flex flex-col gap-1.5">
                {fieldLabel(<ListTodo size={14} aria-hidden="true" />, "Liste")}
                {/* Verschieben ja, herausnehmen nein: eine Aufgabe gehört immer
                    in eine Liste, „Ohne Liste" gibt es nicht mehr (der Server
                    weist `list_id: null` mit 400 ab). */}
                <select
                  value={detail.list_id === null ? "" : String(detail.list_id)}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    const v = (e.target as HTMLSelectElement).value;
                    if (v === "") return;
                    void patch({ list_id: Number(v) });
                  }}
                  class={`${railInput} ${readOnly ? "opacity-70 cursor-default" : ""}`}
                >
                  {/* Nur für Altbestand/Ladezustand: ohne diese Zeile würde das
                      Feld eine fremde Liste anzeigen, wenn list_id fehlt. */}
                  {detail.list_id === null && <option value="">—</option>}
                  {lists.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Status — kommt aus dem Status-Set der Liste der Aufgabe */}
              {listStatuses.length > 0 && (
                <label class="flex flex-col gap-1.5">
                  {fieldLabel(<CircleDot size={14} aria-hidden="true" />, "Status")}
                  <select
                    value={detail.status_id != null ? String(detail.status_id) : ""}
                    disabled={readOnly}
                    onChange={readOnly ? undefined : (e) => {
                      const v = (e.target as HTMLSelectElement).value;
                      if (v === "") return;
                      void patch({ status_id: Number(v) });
                    }}
                    class={`${railInput} ${readOnly ? "opacity-70 cursor-default" : ""}`}
                  >
                    {detail.status_id == null && <option value="">—</option>}
                    {listStatuses.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* Zuständig — only for space lists. Mehrere Personen (#854); die
                  Kandidaten sind hart auf die Space-Mitglieder begrenzt, alles
                  andere lehnt der Server mit 400 ab. */}
              {isSpaceList && (
                <div class="flex flex-col gap-1.5">
                  {fieldLabel(<UserIcon size={14} aria-hidden="true" />, "Zuständig")}
                  {!readOnly && spaceMembers.length === 0 ? (
                    <span class="text-sm text-[color:var(--color-on-surface-variant)]">
                      Keine zuweisbaren Personen — dem Space eine Abteilung
                      zuweisen, z.&nbsp;B. „Gesamtorganisation" für alle
                      (Admin → Spaces → Abteilungen).
                    </span>
                  ) : !readOnly ? (
                    <AssigneePicker
                      value={detailAssigneeIds}
                      candidateIds={spaceMembers.map((m) => m.user_id)}
                      label="Zuständige"
                      onChange={(next) =>
                        updatePersonalTask(taskId, { assignee_ids: next })
                          .then(() => reload())
                          // PATCH -> api() toastet einen Fehlschlag bereits;
                          // nur die unhandled rejection abfangen.
                          .catch(() => {})
                      }
                    />
                  ) : detailAssigneeIds.length === 0 ? (
                    <span class="text-sm text-[color:var(--color-on-surface)]">—</span>
                  ) : (
                    <span class="flex items-center gap-2">
                      <AvatarStack userIds={detailAssigneeIds} size="md" />
                      <span class="text-sm text-[color:var(--color-on-surface)] truncate">
                        {detailAssigneeIds.length === 1
                          ? (spaceMembers.find((m) => m.user_id === detailAssigneeIds[0])
                              ?.name ?? "—")
                          : `${detailAssigneeIds.length} Personen`}
                      </span>
                    </span>
                  )}
                </div>
              )}

              {/* Recurrence — hidden when this task is itself a step (the
                  server rejects recurrence-on-steps). */}
              {detail.parent_id == null && (
              <div class="flex flex-col gap-1.5">
                {fieldLabel(<Repeat size={14} aria-hidden="true" />, "Wiederholen")}
                <div class="flex items-center gap-2">
                  <select
                    value={detail.recurrence?.rule ?? "none"}
                    aria-label="Wiederholen"
                    disabled={readOnly}
                    onChange={readOnly ? undefined : (e) => void handleRecurrence((e.target as HTMLSelectElement).value)}
                    class={`flex-1 ${railInput} ${readOnly ? "opacity-70" : ""}`}
                  >
                    <option value="none">Keine</option>
                    <option value="daily">Täglich</option>
                    <option value="weekly">Wöchentlich</option>
                    <option value="monthly">Monatlich</option>
                  </select>
                  {detail.recurrence && (
                    <input
                      type="number"
                      min={1}
                      value={detail.recurrence.interval_count}
                      disabled={readOnly}
                      onChange={readOnly ? undefined : (e) => void handleInterval(Number((e.target as HTMLInputElement).value))}
                      class={`w-16 shrink-0 ${railInput} ${readOnly ? "opacity-70" : ""}`}
                      aria-label="Intervall"
                    />
                  )}
                </div>
              </div>
              )}

              {/* Reminder — hidden for read-only */}
              {!readOnly && (
                <div class="flex flex-col gap-1.5">
                  {fieldLabel(<Bell size={14} aria-hidden="true" />, "Erinnerung")}
                  {/* Leer zeigt das Feld einen eigenen Hinweis statt des
                      Browser-Musters „tt.mm.jjjj, --:--" (gleiche Technik wie
                      TaskDateCell); beim Fokus erscheint das native Feld. */}
                  <span class="relative block">
                    <input
                      type="datetime-local"
                      value={reminderAt}
                      aria-label="Erinnerung"
                      onInput={(e) => setReminderAt((e.target as HTMLInputElement).value)}
                      class={`peer ${railInput} ${reminderAt ? "" : DATE_EMPTY_SUPPRESS_CLS}`}
                    />
                    {!reminderAt && (
                      <span
                        class="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-[color:var(--color-placeholder)] peer-focus:opacity-0"
                        aria-hidden="true"
                      >
                        Datum und Uhrzeit wählen
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleReminder()}
                    disabled={!reminderAt}
                    class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50"
                  >
                    Erinnerung setzen
                  </button>
                  <PendingReminders personalTaskId={taskId} />
                  <button
                    type="button"
                    onClick={() => setFollowUpOpen(true)}
                    class="mt-1 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)]"
                  >
                    Nachfrage an Kollegen planen
                  </button>
                </div>
              )}

              {/* Anhänge — in der rechten Spalte unter „Erinnerung" */}
              <AttachmentsSection personalTaskId={taskId} />
            </aside>
          </div>
        </div>
      )}
      {followUpOpen && detail && (
        <FollowUpComposer
          personalTaskId={taskId}
          contextLabel={detail.title}
          onClose={() => setFollowUpOpen(false)}
        />
      )}
      {showMergeDialog && detail && (
        <MergeTaskDialog
          kind="personal_task"
          source={{ id: taskId, title: detail.title }}
          onDone={onMerged}
          onClose={() => setShowMergeDialog(false)}
        />
      )}
    </Modal>
  );
}
